import { Alert, Box, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';
import { resolveSlugSourceName } from '../../utils/registerWizardModel';
import { Button } from '../ui/Button';
import { RegisterSectionCard } from './RegisterSectionCard';
import { TrialSummaryCard } from './TrialSummaryCard';

const { semantic } = colorTokens;

/**
 * Step 3 — tenant identity. Only tenant_name / tenant_slug / primary_domain are
 * sent; all three are accepted by PublicTrialRegistrationRequest.
 */
export function RegisterStepTenant({
  form,
  stepErrors,
  slugPreview,
  slugPreviewLoading,
  slugPreviewError,
  showAdvanced,
  onToggleAdvanced,
  onFieldChange,
  preview,
  previewLoading,
  previewError,
  selectedModules,
  billingInterval,
  usersCount,
  smsNotificationsEnabled,
  startMode,
}) {
  const { t } = useTranslation(['onboarding', 'auth', 'register', 'common']);

  const previewDomain = form.primary_domain || slugPreview?.primary_domain || '';
  const previewUrl = previewDomain ? `https://${previewDomain}` : '';

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems="flex-start"
      data-testid="register-step-tenant"
    >
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <RegisterSectionCard
          title={t('onboarding:register.wizard.tenant_section_title')}
          helper={t('onboarding:register.wizard.tenant_section_helper')}
          testId="register-tenant-identity"
        >
          <Stack spacing={1.5}>
            {slugPreviewLoading ? (
              <Typography sx={{ fontSize: 13, color: semantic.text.muted }}>
                {t('common:loading')}
              </Typography>
            ) : null}
            {!slugPreviewLoading && !resolveSlugSourceName(form) ? (
              <Alert severity="info">{t('register:generatedAfterCompanyName')}</Alert>
            ) : null}

            <Box
              data-testid="register-tenant-url-preview"
              sx={{
                p: 1.5,
                borderRadius: `${radiusTokens.md}px`,
                border: `1px solid ${semantic.border.subtle}`,
                bgcolor: semantic.bg.surface,
              }}
            >
              <Typography sx={{ fontSize: 12, color: semantic.text.muted, mb: 0.5 }}>
                {t('onboarding:register.wizard.tenant_url_preview')}
              </Typography>
              <Typography
                data-testid="register-tenant-url-value"
                sx={{
                  fontFamily: typographyTokens.fontFamilyMono,
                  fontSize: 14,
                  color: semantic.text.primary,
                  wordBreak: 'break-all',
                }}
              >
                {previewUrl || t('common:empty_value')}
              </Typography>
            </Box>

            {slugPreviewError ? (
              <Alert severity="warning">{t('register:slugPreviewUnavailable')}</Alert>
            ) : null}
            {slugPreview?.collision_resolved ? (
              <Alert severity="info">
                {t('register:slugCollisionResolved', { slug: slugPreview.slug })}
              </Alert>
            ) : null}

            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('register:tenantNameGenerated')}
                {': '}
                <strong>{form.tenant_name || slugPreview?.tenant_name || t('common:empty_value')}</strong>
              </Typography>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('register:slugGenerated')}
                {': '}
                <strong data-testid="register-tenant-slug-value">
                  {form.tenant_slug || slugPreview?.slug || t('common:empty_value')}
                </strong>
              </Typography>
            </Stack>

            {stepErrors.tenant_slug ? (
              <Alert severity="warning" data-testid="register-tenant-slug-error">
                {t('onboarding:register.wizard.tenant_slug_invalid')}
              </Alert>
            ) : null}
          </Stack>
        </RegisterSectionCard>

        <RegisterSectionCard
          title={t('onboarding:register.wizard.tenant_advanced_title')}
          helper={t('register:technicalDetailsHelp')}
          testId="register-tenant-advanced"
        >
          <Button
            size="small"
            variant="Ghost"
            onClick={onToggleAdvanced}
            data-testid="register-tenant-advanced-toggle"
          >
            {showAdvanced
              ? t('register:hideTechnicalDetails')
              : t('register:editTechnicalDetails')}
          </Button>
          {showAdvanced ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label={t('auth:tenant_name')}
                value={form.tenant_name}
                onChange={onFieldChange('tenant_name')}
                inputProps={{ 'data-testid': 'register-tenant-name' }}
              />
              <TextField
                label={t('auth:tenant_slug')}
                value={form.tenant_slug}
                onChange={onFieldChange('tenant_slug')}
                error={Boolean(stepErrors.tenant_slug)}
                helperText={t('onboarding:register.wizard.tenant_slug_helper')}
                inputProps={{ 'data-testid': 'register-tenant-slug' }}
              />
              <TextField
                label={t('auth:primary_domain')}
                value={form.primary_domain}
                onChange={onFieldChange('primary_domain')}
                helperText={t('register:domainReadonlyHelp')}
                inputProps={{ 'data-testid': 'register-primary-domain' }}
              />
            </Stack>
          ) : null}
        </RegisterSectionCard>
      </Stack>

      <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
        <TrialSummaryCard
          preview={preview}
          loading={previewLoading}
          error={previewError}
          selectedModules={selectedModules}
          billingInterval={billingInterval}
          usersCount={usersCount}
          smsNotificationsEnabled={smsNotificationsEnabled}
          startMode={startMode}
        />
      </Box>
    </Stack>
  );
}

export default RegisterStepTenant;
